// =============================================================================
// BP-08-05: Vendor Portal Frontend - VerificationStatus Component
// =============================================================================

import React from 'react';
import { CheckCircle, Clock, AlertCircle, Upload, Shield } from 'lucide-react';
import type { VendorProfile } from '@/features/vendor/types/vendorTypes';

interface VerificationStatusProps {
  profile: VendorProfile;
  onUploadDocuments?: () => void;
}

export function VerificationStatus({ profile, onUploadDocuments }: VerificationStatusProps) {
  if (profile.isVerified) {
    return (
      <div className="bg-green-50 rounded-xl p-4 border border-green-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <h3 className="font-semibold text-green-800">Verified Vendor</h3>
            <p className="text-sm text-green-700">
              Verified on {new Date(profile.verifiedAt!).toLocaleDateString()}
            </p>
          </div>
          <Shield className="w-8 h-8 text-green-500 ml-auto" />
        </div>
      </div>
    );
  }

  const pendingDocs = profile.verificationDocuments.filter((d) => d.status === 'pending');
  const rejectedDocs = profile.verificationDocuments.filter((d) => d.status === 'rejected');

  if (rejectedDocs.length > 0) {
    return (
      <div className="bg-red-50 rounded-xl p-4 border border-red-200">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-red-800">Verification Issue</h3>
            <p className="text-sm text-red-700 mt-1">
              Some documents were rejected. Please review and resubmit.
            </p>
            <ul className="mt-2 space-y-1">
              {rejectedDocs.map((doc) => (
                <li key={doc.id} className="text-sm text-red-600">
                  • {doc.type}: {doc.notes || 'No reason provided'}
                </li>
              ))}
            </ul>
            <button
              onClick={onUploadDocuments}
              className="mt-3 flex items-center gap-2 px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors"
            >
              <Upload className="w-4 h-4" />
              Resubmit Documents
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (pendingDocs.length > 0) {
    return (
      <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Clock className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-amber-800">Verification In Progress</h3>
            <p className="text-sm text-amber-700 mt-1">
              Your documents are being reviewed. This typically takes 1-2 business days.
            </p>
            <div className="mt-3 space-y-2">
              {pendingDocs.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center gap-2 text-sm text-amber-700"
                >
                  <Clock className="w-4 h-4" />
                  <span>{doc.type}</span>
                  <span className="text-amber-500">• Pending review</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No documents submitted
  return (
    <div className="bg-blue-50 rounded-xl p-4 border border-blue-200">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
          <Shield className="w-5 h-5 text-blue-600" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-blue-800">Get Verified</h3>
          <p className="text-sm text-blue-700 mt-1">
            Verified vendors get a badge, higher visibility, and increased customer trust.
          </p>
          <button
            onClick={onUploadDocuments}
            className="mt-3 flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Upload className="w-4 h-4" />
            Submit Verification Documents
          </button>
        </div>
      </div>
    </div>
  );
}

export default VerificationStatus;

